import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetchViaPost } from "../helpers/fetchViaPost";
import { readDataMaintenanceResponse } from "../helpers/dataMaintenanceResponse";
export function maintenanceReason(reason, en = false) {
  const rules = [
    [
      /current_season_unavailable/,
      [
        "Keine aktuelle Saison verfügbar; Wettbewerb ausgelassen.",
        "No current season available; competition skipped.",
      ],
    ],
    [
      /not_played/,
      [
        "Noch nicht gespielt; keine Statistiklücke.",
        "Not played; no statistics gap.",
      ],
    ],
    [
      /statistic_model|INITIALIZE|REPLAY|CURSOR|SEED|HISTORICAL/,
      [
        "Statistikmodell: historische Grundlage oder chronologische Fortsetzung fehlt.",
        "Statistics model: historical basis or chronological continuation unavailable.",
      ],
    ],
    [
      /prediction|model_basis|parameters/,
      [
        "Gespeicherte Prognose oder damalige Modellgrundlage fehlt.",
        "Stored prediction or historical model basis is missing.",
      ],
    ],
    [
      /statistics|STATISTICS/,
      [
        "Spielstatistiken fehlen oder sind beim Anbieter unvollständig.",
        "Match statistics are missing or incomplete at the provider.",
      ],
    ],
    [
      /prematch|market|one_x_two/i,
      [
        "Keine ausreichend belegte Pre-Match-Marktgrundlage.",
        "No sufficiently verified pre-match market basis.",
      ],
    ],
    [
      /CONCURRENT|SCOPE|ACCESS/,
      [
        "Umfang oder Daten wurden zwischenzeitlich verändert; Lauf angehalten.",
        "Scope or data changed in the meantime; run paused.",
      ],
    ],
    [
      /season_base|actual|record_invalid/,
      [
        "Saison-Grunddaten fehlen oder benötigen eine gesonderte Prüfung.",
        "Season foundations are missing or need a separate review.",
      ],
    ],
  ];
  return (
    rules.find(([re]) => re.test(reason || ""))?.[1][en ? 1 : 0] ||
    (en
      ? "This step could not be safely completed. Details are available below."
      : "Dieser Schritt konnte nicht sicher abgeschlossen werden. Details stehen unten.")
  );
}
export default function AutomaticDataMaintenance({
  state,
  onClose,
  targets = [],
  onCompleted,
}) {
  const { i18n } = useTranslation();
  const en = !i18n.language.startsWith("de"),
    t = (de, english) => (en ? english : de);
  const [job, setJob] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const generation = useRef(0),
    completed = useRef(null),
    onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const request = useCallback(async (operation, extra = {}) => {
    const response = await fetchViaPost(
      "maintain-my-data-season",
      JSON.stringify({ automatic: true, operation, ...(operation === "status" ? {job_lane:"history"} : {}), ...extra })
    );
    const result = await readDataMaintenanceResponse(
      response,
      "maintain-my-data-season"
    );
    if (result.success !== true)
      throw Error(result.reason || result.message || "MAINTENANCE_FAILED");
    return result;
  }, []);
  useEffect(() => {
    if (!state.open) return;
    let stopped = false,
      timer;
    const current = ++generation.current;
    setError("");
    async function poll() {
      try {
        const result = await request("status");
        if (!stopped && generation.current === current) {
          setJob(result);
          if (
            ["complete", "complete_with_gaps", "restored"].includes(
              result.status
            ) &&
            completed.current !== result.id
          ) {
            completed.current = result.id;
            await onCompletedRef.current?.(result);
          }
          timer = setTimeout(
            poll,
            ["running", "scanning", "restoring"].includes(result.status)
              ? 4000
              : 15000
          );
        }
      } catch (e) {
        if (!stopped) {
          setError(e.message);
          timer = setTimeout(poll, 15000);
        }
      }
    }
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
      generation.current++;
    };
  }, [state.open, request]);
  async function action(operation) {
    setBusy(true);
    setError("");
    try {
      setJob(await request(operation, { run_id: job?.id, scope: targets }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const running = ["running", "scanning", "restoring"].includes(job?.status),
    ready = job?.status === "ready",
    finished = ["complete", "complete_with_gaps", "restored"].includes(
      job?.status
    ),
    scope = job?.scope || targets,
    counts = job?.counts || {};
  return (
    <Dialog
      open={state.open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: "#122332",
          color: "#f3f6fa",
          maxHeight: "calc(100% - 128px)",
        },
      }}
    >
      <DialogTitle>{t("Datenpflege", "Data maintenance")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography>
            {t(
              "Prüft den ausgewählten Bestand und ergänzt sicher rekonstruierbare Daten. Gültige gespeicherte Prognosen bleiben erhalten; noch offene Evaluationen schreiben die Modelle chronologisch fort.",
              "Checks the selected scope and repairs data where supported by evidence. Valid stored predictions are preserved; pending evaluations advance the models chronologically."
            )}
          </Typography>
          <Box aria-label={t("Automatikstatus", "Automation status")}>
            <Typography fontWeight={700}>
              {t("Hintergrundpflege", "Background maintenance")}
            </Typography>
            <Typography variant="body2">
              {job?.automation?.enabled
                ? t("Aktiv · alle sechs Stunden", "Active · every six hours")
                : t("Noch nicht aktiviert", "Not activated yet")}
            </Typography>
            {[
              ["last_check_at", "Letzter Datenabgleich", "Last data check"],
              [
                "last_success_at",
                "Letzter vollständiger Lauf",
                "Last complete run",
              ],
              ["next_run_at", "Nächster Lauf", "Next run"],
            ].map(([key, de, english]) => (
              <Typography key={key} variant="body2">
                {t(de, english)}:{" "}
                {job?.automation?.[key]
                  ? new Date(job.automation[key]).toLocaleString(
                      en ? "en-GB" : "de-DE"
                    )
                  : "—"}
              </Typography>
            ))}
            <Typography variant="caption">
              {t("Prognosehorizont", "Forecast horizon")}:{" "}
              {job?.automation?.horizon_days || 14}{" "}
              {t(
                "Tage plus vollständiger nächster Spieltag",
                "days plus the complete next round"
              )}
            </Typography>
          </Box>
          <Box>
            <Typography fontWeight={700}>
              {t("Umfang", "Scope")}: {scope.length}{" "}
              {t("Wettbewerbe", "competitions")}
            </Typography>
            {scope.map((s) => (
              <Typography variant="body2" key={`${s.league_id}:${s.season_id}`}>
                {s.league_name || s.league_id}
                {s.season_name ? ` · ${s.season_name}` : ""}
                {s.products
                  ? ` · ${
                      Object.values(s.products).every((value) =>
                        ["complete", "not_entitled", "not_applicable"].includes(
                          value
                        )
                      )
                        ? t("vollständig", "complete")
                        : t("mit offenen Lücken", "with gaps")
                    }`
                  : ""}
                {s.fixtures != null
                  ? ` · ${s.fixtures} ${t("Spiele", "fixtures")}`
                  : ""}
              </Typography>
            ))}
          </Box>
          {job?.estimate_seconds != null && (
            <Typography>
              {t("Geschätzte Dauer", "Estimated duration")}:{" "}
              {Math.max(1, Math.ceil(job.estimate_seconds / 60))} min{" "}
              {t(
                "(abhängig von Anbieter und Datenlücken)",
                "(depends on provider and data gaps)"
              )}
            </Typography>
          )}
          {ready && (
            <Typography fontWeight={700}>
              {t("Datenpflege starten?", "Start data maintenance?")}
            </Typography>
          )}
          {running && (
            <Box role="status">
              <Typography>
                {job.status === "scanning"
                  ? t("Umfang wird geprüft", "Checking scope")
                  : t("Datenpflege läuft", "Maintenance running")}{" "}
                · {job.current_activity}
              </Typography>
              <Typography>
                {job.status === "scanning"
                  ? `${job.scanned} / ${scope.length}`
                  : `${job.current} / ${job.total}`}{" "}
                ·{" "}
                {
                  {
                    source: t("Quelldaten", "Source data"),
                    actual: t("Reale Tabellen", "Actual tables"),
                    result: t(
                      "Chronologische Evaluation",
                      "Chronological evaluation"
                    ),
                    verify: t(
                      "Vollständigkeit prüfen",
                      "Checking completeness"
                    ),
                    model: t(
                      "Chronologisches Statistikmodell",
                      "Chronological statistics model"
                    ),
                    derived: t(
                      "Should, Markt und Aggregate",
                      "Should, market and aggregates"
                    ),
                  }[job.phase]
                }
              </Typography>
              <LinearProgress
                variant={
                  job.total && job.status === "running"
                    ? "determinate"
                    : "indeterminate"
                }
                value={Math.min(
                  100,
                  (100 * (job.current || 0)) / Math.max(1, job.total || 0)
                )}
              />
              <Typography variant="caption">
                {t(
                  "Der Lauf läuft auch nach Schließen oder Reload weiter.",
                  "The run continues after closing this view or reloading."
                )}
              </Typography>
            </Box>
          )}
          {(finished || job?.status === "paused") && (
            <Typography role="status">
              {job.status === "restored"
                ? t(
                    "Vorheriger Stand wiederhergestellt.",
                    "Previous state restored."
                  )
                : job.status === "complete"
                ? t("Datenpflege abgeschlossen.", "Maintenance completed.")
                : job.status === "paused"
                ? t("Lauf angehalten.", "Run paused.")
                : t(
                    "Abgeschlossen; einige Grundlagen fehlen weiterhin.",
                    "Completed; some source data remains unavailable."
                  )}
            </Typography>
          )}
          {job?.started_at && (
            <Box>
              {[
                ["checked", "Geprüft", "Checked"],
                ["reloaded", "Nachgeladen", "Reloaded"],
                ["corrected", "Korrigiert", "Corrected"],
                ["unchanged", "Unverändert", "Unchanged"],
                ["unrepairable", "In diesem Lauf nicht vervollständigbar", "Unavailable in this run"],
                ["deferred_known", "Bekannte zurückgestellte Datenlücken", "Known deferred gaps"],
                ["technical_deferred", "Technisch zurückgestellte Aufgaben", "Technically deferred tasks"],
              ].map(([key, de, english]) => (
                <Typography key={key}>
                  {t(de, english)}: {counts[key] || 0}
                </Typography>
              ))}
            </Box>
          )}
          {!!job?.issues?.length && (
            <Box>
              {[
                ...new Set(
                  job.issues.map((x) => maintenanceReason(x.reason, en))
                ),
              ].map((text) => (
                <Typography key={text} variant="body2">
                  {text}
                </Typography>
              ))}
            </Box>
          )}
          {error && (
            <Alert severity="error">
              {maintenanceReason(error, en)} ({error})
            </Alert>
          )}
          {job?.id && (
            <Box component="details">
              <Typography component="summary" sx={{ cursor: "pointer" }}>
                {t("Technische Details", "Technical details")}
              </Typography>
              <Typography
                component="pre"
                variant="caption"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {JSON.stringify(
                  {
                    run_id: job.id,
                    status: job.status,
                    phase: job.phase,
                    counts: job.counts,
                    issues: job.issues,
                    last_error: job.last_error,
                  },
                  null,
                  2
                )}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Schließen", "Close")}</Button>
        {!running && job?.automation?.activation_available && (
          <Button
            disabled={busy || !targets.length}
            title={t(
              "Verwendet denselben geprüften Pflegeablauf für die ausgewählten Ligen.",
              "Uses the same verified maintenance workflow for the selected leagues."
            )}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                setJob(
                  await request("configure", {
                    scope: targets,
                    enabled: !job?.automation?.enabled,
                  })
                );
              } catch (e) {
                setError(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {job?.automation?.enabled
              ? t("Automatik deaktivieren", "Disable automation")
              : t("Automatik aktivieren", "Enable automation")}
          </Button>
        )}
        {!running && !ready && job?.status !== "paused" && (
          <Button
            disabled={busy || !targets.length}
            onClick={() => action("preview")}
            variant="contained"
          >
            {t("Umfang prüfen", "Check scope")}
          </Button>
        )}
        {ready && (
          <Button
            disabled={busy}
            onClick={() => action("start")}
            variant="contained"
          >
            {t("Datenpflege starten", "Start maintenance")}
          </Button>
        )}
        {job?.status === "running" && (
          <Button disabled={busy} onClick={() => action("pause")}>
            {t("Pausieren", "Pause")}
          </Button>
        )}
        {job?.status === "paused" && (
          <Button disabled={busy} onClick={() => action("resume")}>
            {t("Fortsetzen", "Resume")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
